import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  EmailVerification,
  IEmailABTestSettings,
  MailrailBaseEmailCampaign,
  Template,
} from '@feathr/blackbox';
import { CardV2 as Card, Fieldset, Form, Input } from '@feathr/components';
import type { ICampaignValidationErrors } from '@feathr/extender/App/EventsPage/CampaignSummary';
import TemplateMergefieldDefaults from '@feathr/extender/App/TemplatePage/TemplateMetadataForm/TemplateMergefieldDefaults';
import { useStore } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

import EmailTemplateConfig from '../EmailTemplateConfig';
import SenderInformationConfig from '../SenderInformationConfig';
import MailrailABTestCard from './MailrailABTestCard';

interface IProps {
  // The A/B Test card renders only when both are passed; the wizard passes them when A/B testing is available.
  abTest?: IEmailABTestSettings;
  campaign: MailrailBaseEmailCampaign;
  disabled: boolean;
  onChangeABTest?: (abTest: IEmailABTestSettings) => void;
  onNext: () => void;
  validationErrors: ICampaignValidationErrors;
  // The following are not used by Autosend campaigns.
  isVerifiedDomainSender?: boolean;
}

interface IErrors extends TValidateGrouped {
  name?: string[];
  template?: string[];
  from_address?: string[];
  from_name?: string[];
  'contact_info.company_name'?: string[];
  'contact_info.premise1'?: string[];
  'contact_info.locality'?: string[];
  'contact_info.administrative_area_name'?: string[];
  'contact_info.postal_code'?: string[];
  'contact_info.country_code'?: string[];
  status?: string[];
}

export function validateStepConfigureDetails(
  campaign: MailrailBaseEmailCampaign,
  emailVerification?: EmailVerification,
  template?: Template,
): IErrors {
  const errors: IErrors = {};
  const campaignErrors = campaign.validate(
    [
      'name',
      'from_address',
      'from_name',
      'contact_info.company_name',
      'contact_info.premise1',
      'contact_info.locality',
      'contact_info.administrative_area_name',
      'contact_info.postal_code',
      'contact_info.country_code',
    ],
    false,
    'grouped',
  ).errors;
  Object.keys(campaignErrors).forEach((key) => {
    errors[key] = campaignErrors[key];
  });

  const emailVerificationErrors =
    emailVerification?.validate<IErrors>(['status'], false, 'grouped').errors ??
    (observable({}) as IErrors);
  if (emailVerificationErrors.status === undefined) {
    emailVerificationErrors.status = [] as string[];
  }
  const status = emailVerification?.get('status') ?? 'Unverified';
  if (status === 'Success') {
    // Do nothing.
  } else if (status === 'Pending') {
    emailVerificationErrors.status.push('Your email is still pending verification.');
  } else {
    emailVerificationErrors.status.push('Your email is unverified.');
  }
  Object.keys(emailVerificationErrors).forEach((key) => {
    errors[key] = emailVerificationErrors[key];
  });

  const templateErrors: string[] = [];
  if (!template) {
    templateErrors.push('A template needs to be selected.');
  }
  if (template?.isEphemeral) {
    templateErrors.push('The template needs to be saved.');
  }
  if (!template?.get('subject')) {
    templateErrors.push('The email must have a subject.');
  }
  if (templateErrors.length > 0) {
    errors.template = templateErrors;
  }

  return errors;
}

function MailrailCampaignStepConfigureDetails({
  abTest,
  campaign,
  disabled,
  isVerifiedDomainSender = false,
  onChangeABTest,
  validationErrors,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Templates } = useStore();

  const templateId = campaign.get('template_id');
  const template = templateId ? Templates.get(templateId) : undefined;

  return (
    <Form
      label={
        campaign.isMailrailPartnerMessage
          ? t('Edit Message: Sender Info')
          : t('Edit Campaign: Sender Info')
      }
    >
      <Card>
        <Card.Header
          title={
            campaign.isMailrailPartnerMessage ? t('Message Information') : t('Campaign Information')
          }
        />
        <Card.Content>
          <Fieldset>
            <Input
              attribute={'name'}
              disabled={disabled}
              label={campaign.isMailrailPartnerMessage ? t('Message name') : t('Campaign name')}
              model={campaign}
              name={'campaign_name'}
              required={true}
              type={'text'}
              validationError={flattenError(validationErrors.name)}
            />
          </Fieldset>
        </Card.Content>
      </Card>
      {abTest && onChangeABTest && (
        <MailrailABTestCard disabled={disabled} onChange={onChangeABTest} value={abTest} />
      )}
      <SenderInformationConfig
        disabled={disabled}
        isVerifiedDomainSender={isVerifiedDomainSender}
        mailing={campaign}
        validationErrors={validationErrors}
      />
      <EmailTemplateConfig
        campaign={campaign}
        disabled={disabled}
        mailing={campaign}
        template={template}
        validationErrors={validationErrors}
      />
      {template && !!template.get('used_mergefields').length && (
        <TemplateMergefieldDefaults
          disabled={disabled || template.isReadOnly}
          template={template}
        />
      )}
    </Form>
  );
}

export default observer(MailrailCampaignStepConfigureDetails);
